import React, { useEffect, useState } from "react";
import "./Product.css";
import { AiOutlineEdit, AiOutlineDelete, AiOutlinePlus, AiOutlineCheck, AiOutlineClose, AiOutlinePrinter } from "react-icons/ai";
import {getProducts, deleteProduct, updateProduct, getProductBarcode} from "../../services/productService";
import { getCategories } from "../../services/categoryService";
import ProductModal from "./ProductModal";
import CategoryModal from "./CategoryModal";
import Popup from "../../components/ui/Popup";

function ProductList() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [statusModal, setStatusModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);
    const [popupMessage, setPopupMessage] = useState("");
    const [popupType, setPopupType] = useState("success");
    const [isLoading, setIsLoading] = useState(false);
    const [barcodes, setBarcodes] = useState({});
    const [filters, setFilters] = useState({
        category: "",
        brand: "",
        country: "",
        searchQuery: "",
    });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const fetchProducts = async () => {
        setIsLoading(true);
        try {
            const productData = await getProducts();

            const barcodeData = {};
            await Promise.all(
                productData.map(async (product) => {
                    const barcodeUrl = await getProductBarcode(product.id);
                    if (barcodeUrl) {
                        barcodeData[product.id] = barcodeUrl;
                    }
                })
            );

            setProducts(productData);
            setBarcodes(barcodeData);
        } catch (error) {
            console.error("Failed to fetch products or barcodes:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const data = await getCategories();
            setCategories(data);
        } catch (error) {
            console.error("Failed to fetch categories:", error);
        }
    };

    const showPopup = (message, type = "success") => {
        setPopupMessage(message);
        setPopupType(type);
        setTimeout(() => {
            setPopupMessage("");
        }, 3000);
    };

    const handleDelete = async () => {
        if (!deleteModal) return;
        try {
            await deleteProduct(deleteModal.id);
            setProducts(products.filter(product => product.id !== deleteModal.id));
            showPopup("Product deleted successfully!");
            setDeleteModal(null);
        } catch (error) {
            console.error("Failed to delete product:", error);
            showPopup("Failed to delete product. Please try again.", "error");
        }
    };

    const handleEdit = (product) => {
        setSelectedProduct(product);
        setIsProductModalOpen(true);
    };

    const handleStatusChange = async () => {
        if (!statusModal) return;
        try {
            const currentProduct = statusModal;
            const newStatus = currentProduct.status === "Y" ||
            currentProduct.status === "1" ||
            currentProduct.status === 1 ? "0" : "1";

            const updatedProduct = {
                name: currentProduct.name,
                categoryId: currentProduct.category_id,
                brand: currentProduct.brand,
                country: currentProduct.country,
                price: parseFloat(currentProduct.price),
                description: currentProduct.description,
                isImported: currentProduct.is_imported === 1 ||
                    currentProduct.is_imported === true,
                status: newStatus
            };

            await updateProduct(currentProduct.id, updatedProduct);

            setProducts(products.map(p =>
                p.id === currentProduct.id
                    ? { ...p, status: newStatus }
                    : p
            ));

            showPopup("Product status updated successfully!");
            setStatusModal(null);
        } catch (error) {
            console.error("Failed to update status:", error);
            showPopup("Failed to update product status. Please try again.", "error");
        }
    };

    const handleFilterChange = (e) => {
        setFilters({ ...filters, [e.target.name]: e.target.value });
        setCurrentPage(1);
    };

    const uniqueValues = (key) => [...new Set(products.map(product => product[key]))];

    const filteredProducts = products.filter((product) => {
        return (
            (!filters.category || product.category_id === parseInt(filters.category)) &&
            (!filters.brand || product.brand === filters.brand) &&
            (!filters.country || product.country === filters.country) &&
            (!filters.searchQuery || product.name.toLowerCase().includes(filters.searchQuery.toLowerCase()))
        );
    });

    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const goToPage = (pageNumber) => {
        setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
    };

    const handlePrintBarcode = async (product) => {
        const barcodeUrl = barcodes[product.id];
        if (!barcodeUrl) {
            console.warn(`No barcode available for product: ${product.name}`);
            return;
        }

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
            <head>
                <title>Barcode</title>
                <style>
                    @media print {
                        body {
                            width: 58mm;
                            height: auto;
                            padding: 10px;
                            margin: 0;
                        }
                        .barcode-container {
                            text-align: center;
                            margin-top: 20px;
                        }
                        .barcode-img {
                            width: 100%;
                            height: auto;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="barcode-container">
                    <img src="${barcodeUrl}" class="barcode-img" alt="Barcode" />
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
        printWindow.close();
    };

    return (
        <div className="content-wrapper">
            <div className="filters">
                <select name="category" value={filters.category} onChange={handleFilterChange}>
                    <option value="">All Categories</option>
                    {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.category_name}</option>
                    ))}
                </select>

                <select name="brand" value={filters.brand} onChange={handleFilterChange}>
                    <option value="">All Brands</option>
                    {uniqueValues("brand")
                        .filter(brand => brand)
                        .map(brand => (
                            <option key={brand} value={brand}>{brand}</option>
                        ))
                    }
                </select>

                <select name="country" value={filters.country} onChange={handleFilterChange}>
                    <option value="">All Countries</option>
                    {uniqueValues("country")
                        .filter(country => country)
                        .map(country => (
                            <option key={country} value={country}>{country}</option>
                        ))
                    }
                </select>

                <input
                    type="text"
                    name="searchQuery"
                    placeholder="Search Product..."
                    value={filters.searchQuery}
                    onChange={handleFilterChange}
                />

                <button className="add-product-btn" onClick={() => {
                    setSelectedProduct(null);
                    setIsProductModalOpen(true);
                }}>
                    <AiOutlinePlus className="add-icon" /> Add Product
                </button>

                <button className="add-category-btn" onClick={() => {
                    setSelectedCategory(null);
                    setIsCategoryModalOpen(true);
                }}>
                    <AiOutlinePlus className="add-icon" /> Manage Categories
                </button>
            </div>

            <div className="table-wrapper">
                {isLoading ? (
                    <div className="loading-indicator">Loading products...</div>
                ) : (
                    <table>
                        <thead>
                        <tr>
                            <th>Name</th>
                            <th>Category</th>
                            <th>Brand</th>
                            <th>Country</th>
                            <th>Price</th>
                            <th>Status</th>
                            <th>Barcode</th>
                            <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {paginatedProducts.map((product) => (
                            <tr key={product.id}>
                                <td>{product.name}</td>
                                <td>{product.category_name}</td>
                                <td>{product.brand}</td>
                                <td>{product.country}</td>
                                <td>Rs. {product.price}</td>
                                <td className="status-cell">
                                    <button
                                        className={`status-btn ${Number(product.status) === 1 ? "status-active" : "status-inactive"}`}
                                        onClick={() => setStatusModal(product)}
                                    >
                                        {Number(product.status) === 1 ? (
                                            <><AiOutlineCheck /> Active</>
                                        ) : (
                                            <><AiOutlineClose /> Inactive</>
                                        )}
                                    </button>
                                </td>
                                <td>
                                        {barcodes[product.id] ? (
                                            <img 
                                                src={barcodes[product.id]} 
                                                alt="Barcode" 
                                                style={{ width: "100px", height: "40px", border: "1px solid #ddd", padding: "5px" }}
                                            />
                                        ) : (
                                            "N/A"
                                        )}
                                    </td>
                                <td className="actions">
                                <button
                                            className="action-btn print-btn"
                                            onClick={() => handlePrintBarcode(product)}
                                            style={{ backgroundColor: "#4CAF50", color: "#fff", padding: "5px 10px", border: "none", borderRadius: "4px" }}
                                        >
                                            <AiOutlinePrinter />
                                        </button>
                                    <button
                                        className="action-btn edit-btn"
                                        onClick={() => handleEdit(product)}
                                    >
                                        <AiOutlineEdit />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {paginatedProducts.length === 0 && (
                            <tr>
                                <td colSpan="8" className="no-data">No products found.</td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                )}

                {filteredProducts.length > 0 && (
                    <div className="pagination">
                        <button
                            className="pagination-btn"
                            disabled={currentPage === 1}
                            onClick={() => goToPage(1)}
                        >
                            First
                        </button>
                        <button
                            className="pagination-btn"
                            disabled={currentPage === 1}
                            onClick={() => goToPage(currentPage - 1)}
                        >
                            Previous
                        </button>
                        <div className="page-info">
                            <span>Page {currentPage} of {totalPages}</span>
                            <span className="items-info">
                                Showing {paginatedProducts.length} of {filteredProducts.length} items
                            </span>
                        </div>
                        <button
                            className="pagination-btn"
                            disabled={currentPage === totalPages}
                            onClick={() => goToPage(currentPage + 1)}
                        >
                            Next
                        </button>
                        <button
                            className="pagination-btn"
                            disabled={currentPage === totalPages}
                            onClick={() => goToPage(totalPages)}
                        >
                            Last
                        </button>
                    </div>
                )}
            </div>

            {isProductModalOpen && (
                <ProductModal
                    isOpen={isProductModalOpen}
                    product={selectedProduct}
                    categories={categories}
                    onClose={() => {
                        setIsProductModalOpen(false);
                        setSelectedProduct(null);
                    }}
                    onProductUpdated={fetchProducts}
                    setPopupMessage={showPopup}
                />
            )}

            {isCategoryModalOpen && (
                <CategoryModal
                    isOpen={isCategoryModalOpen}
                    onClose={() => setIsCategoryModalOpen(false)}
                    selectedCategory={selectedCategory}
                    onCategoryUpdated={fetchCategories}
                    setPopupMessage={showPopup}
                />
            )}

            {statusModal && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>Change Product Status</h2>
                        <p>Do you want to change the status of <strong>{statusModal.name}</strong>?</p>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setStatusModal(null)}>Cancel</button>
                            <button className="save-btn" onClick={handleStatusChange}>Confirm</button>
                        </div>
                    </div>
                </div>
            )}

            {deleteModal && (
                <div className="modal">
                    <div className="modal-content">
                        <h2>Confirm Deletion</h2>
                        <p>Are you sure you want to delete <strong>{deleteModal.name}</strong>?</p>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setDeleteModal(null)}>Cancel</button>
                            <button className="delete-btn" onClick={handleDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {popupMessage && (
                <Popup
                    message={popupMessage}
                    type={popupType}
                    onClose={() => setPopupMessage("")}
                />
            )}
        </div>
    );
}

export default ProductList;